import React, { useState } from 'react';
import useModalStore from '../../stores/modalStore';
import { epochToIso8601 } from "../../utils/formatting";
import { LOCATION_NAME_TO_ID } from "../../utils/constants";
import useEventStore from "../../stores/eventStore";
import LoadingSpinner from '../../LoadingSpinner';
import { AnimatePresence, motion } from 'framer-motion';

const TimeOffModal = ({ logTimeOffEvent }) => {
    const { selectedEvent, setSelectedEvent, updateSelectedEventTimestamp, updateSelectedEventLocationId, updateSelectedEventEndTimestamp } = useEventStore();
    const { showTimeOffModal: show, closeModal } = useModalStore();
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!show) return null;

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            await logTimeOffEvent();
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setSelectedEvent((prev) => ({ ...prev, notes: "", event_timestamp: Date.now() }));
        closeModal("timeOffModal");
    };

    const inputClass = "mt-1 w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition text-sm";

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-800">Time Off Request</h3>
                    <button
                        onClick={handleClose}
                        className="!p-1.5 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-gray-100 !text-gray-400 hover:!text-gray-600 !transition-colors"
                    >
                        ✕
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    <label className="block text-sm font-medium text-gray-700">
                        Start Time
                        <input
                            className={inputClass}
                            required
                            type="datetime-local"
                            value={selectedEvent?.event_timestamp ? epochToIso8601(selectedEvent.event_timestamp) : ''}
                            onChange={updateSelectedEventTimestamp}
                        />
                    </label>

                    <label className="block text-sm font-medium text-gray-700">
                        End Time
                        <input
                            className={inputClass}
                            required
                            type="datetime-local"
                            value={selectedEvent?.event_end_timestamp ? epochToIso8601(selectedEvent.event_end_timestamp) : ''}
                            onChange={updateSelectedEventEndTimestamp}
                        />
                    </label>

                    <label className="block text-sm font-medium text-gray-700">
                        Location
                        <select
                            className={inputClass}
                            value={selectedEvent.location_id}
                            onChange={updateSelectedEventLocationId}
                        >
                            <option value="" disabled>Select a location</option>
                            {Object.entries(LOCATION_NAME_TO_ID)
                                .filter(([_, id]) => id !== null)
                                .map(([name, id]) => (
                                    <option key={id} value={id}>{name}</option>
                                ))}
                        </select>
                    </label>

                    <label className="block text-sm font-medium text-gray-700">
                        Notes
                        <textarea
                            className={`${inputClass} resize-y min-h-[60px]`}
                            value={selectedEvent?.notes || ''}
                            placeholder="Optional notes..."
                            onChange={(e) => setSelectedEvent({ ...selectedEvent, notes: e.target.value })}
                        ></textarea>
                    </label>
                </div>

                <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="!px-4 !py-2 !text-sm !font-medium !text-gray-600 !bg-transparent !border !border-gray-200 !rounded-xl hover:!bg-gray-50 !transition-colors"
                    >
                        Cancel
                    </button>
                    <div className="w-[100px] h-[38px] relative">
                        <AnimatePresence mode="wait" initial={false}>
                            {isSubmitting ? (
                                <motion.div
                                    key="spinner"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="absolute inset-0 flex justify-center items-center"
                                >
                                    <LoadingSpinner />
                                </motion.div>
                            ) : (
                                <motion.button
                                    key="button"
                                    onClick={handleSubmit}
                                    className="!absolute !inset-0 !px-4 !py-2 !text-sm !font-semibold !text-white !bg-indigo-600 hover:!bg-indigo-700 !border-none !rounded-xl !shadow-sm hover:!shadow-md !transition-all"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                >
                                    Submit
                                </motion.button>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TimeOffModal;
